import { Stack, TextField, InputAdornment, Button } from "@mui/material";
import {
  Search,
  KeyboardDoubleArrowRight,
  LocationCity,
  Person,
  FilterList,
  Clear,
  Add,
} from "@mui/icons-material";
import { FilterSelect, FilterOption } from "@components";
import {
  useProtocolApiV1ListDataQuery,
  useWebApiV1GetTenantPhysiciansQuery,
  ListDataOptionSerialisedOut,
  UserOptionOut,
} from "@providers/hop-ord-server/api";
import { ListDataCategory } from "@enums";

interface Props {
  searchText: string;
  setSearchText: (searchText: string) => void;
  selectedStatus: string[];
  setSelectedStatus: (selectedStatus: string[]) => void;
  selectedSite: string[];
  setSelectedSite: (selectedSite: string[]) => void;
  selectedPhysician: string[];
  setSelectedPhysician: (selectedPhysician: string[]) => void;
  selectedQuickFilters: string[];
  setSelectedQuickFilters: (selectedQuickFilters: string[]) => void;
  tenantIds: number[];
  visibleColumns: string[];
  setVisibleColumns: (value: string[]) => void;
  showCreateOrderButton?: boolean;
  onCreateOrderClick?: () => void;
}

const sortByName = (a: string, b: string) => a.localeCompare(b);

const STATUS_OPTIONS: FilterOption[] = [
  { label: "Draft", value: "Draft careplans" },
  { label: "New careplans", value: "New careplans" },
  { label: "Ready for planning", value: "Ready for planning" },
  { label: "Planning (Automation)", value: "Planning (Automation)" },
  { label: "Pending plan review", value: "Pending plan review" },
  { label: "Approved", value: "Approved" },
  { label: "Rejected", value: "Rejected" },
  { label: "Handover complete", value: "Handover complete" },
];

const QUICK_FITLERS: { label: string; value: string; tooltip?: string }[] = [
  { label: "Unread comments", value: "Unread comments" },
  { label: "Prescription changes", value: "Prescription changes" },
  { label: "Test view", value: "Test view", tooltip: "Show test cases only" },
];

const SearchAndFilterBar = ({
  searchText,
  setSearchText,
  selectedStatus,
  setSelectedStatus,
  selectedSite,
  setSelectedSite,
  selectedPhysician,
  setSelectedPhysician,
  selectedQuickFilters,
  setSelectedQuickFilters,
  tenantIds,
  showCreateOrderButton,
  onCreateOrderClick,
}: Props) => {
  const { data: treatingDepartments } = useProtocolApiV1ListDataQuery({
    categories: [ListDataCategory.TREATING_DEPARTMENT],
    tenantIds: tenantIds ?? [],
  });
  const { data: physicians } = useWebApiV1GetTenantPhysiciansQuery({});

  const siteOptions =
    treatingDepartments
      ?.map((listData: ListDataOptionSerialisedOut) => {
        if (
          treatingDepartments?.filter(
            (option) => option?.value === listData?.value,
          ).length >= 2
        ) {
          const tenantName = listData?.tenantName.split(":").at(-1);
          return {
            value: listData?.value,
            label: listData?.value + " (" + tenantName + ")",
            tenantId: listData?.tenantId,
          };
        }
        return {
          value: listData?.value,
          label: listData?.value,
          tenantId: listData?.tenantId,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label)) || [];

  const physicianOptions =
    physicians
      ?.map((physician: UserOptionOut) => ({
        label: physician?.name,
        value: String(physician?.id),
      }))
      .sort((a, b) => sortByName(a.label, b.label)) || [];

  const clearAllFilters = () => {
    setSelectedStatus([]);
    setSelectedSite([]);
    setSelectedPhysician([]);
    setSelectedQuickFilters([]);
  };

  const anyFilterSelected =
    selectedStatus.length > 0 ||
    selectedSite.length > 0 ||
    selectedPhysician.length > 0 ||
    selectedQuickFilters.length > 0;

  return (
    <>
      <Stack direction="row" alignItems="center" gap={1}>
        <TextField
          id="search-text"
          placeholder="Search patients..."
          variant="outlined"
          value={searchText}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setSearchText(event.target.value);
          }}
          sx={{
            width: "325px",
            marginRight: 2,
            "& .MuiOutlinedInput-root": {
              height: "36px",
            },
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="primary" />
                </InputAdornment>
              ),
            },
          }}
        />
        <FilterSelect
          label="Status"
          options={STATUS_OPTIONS}
          selectedOptions={selectedStatus}
          setSelectedOptions={setSelectedStatus}
          startIcon={<KeyboardDoubleArrowRight />}
        />
        <FilterSelect
          label="Treating clinic"
          options={siteOptions}
          selectedOptions={selectedSite}
          setSelectedOptions={setSelectedSite}
          startIcon={<LocationCity />}
        />
        <FilterSelect
          label="Physician"
          options={physicianOptions}
          selectedOptions={selectedPhysician}
          setSelectedOptions={setSelectedPhysician}
          startIcon={<Person />}
        />
        <FilterSelect
          label="Quick Filters"
          options={QUICK_FITLERS}
          selectedOptions={selectedQuickFilters}
          setSelectedOptions={setSelectedQuickFilters}
          startIcon={<FilterList />}
        />
        {anyFilterSelected && (
          <Button
            disabled={!anyFilterSelected}
            startIcon={<Clear />}
            onClick={clearAllFilters}
          >
            Clear Filters
          </Button>
        )}
        {showCreateOrderButton && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={onCreateOrderClick}
            sx={{
              height: "36px",
              marginLeft: "auto",
            }}
          >
            Create New Plan
          </Button>
        )}
      </Stack>
    </>
  );
};

export default SearchAndFilterBar;
